import { Star, Quote, ArrowUpRight, ShieldCheck } from "lucide-react";
import { SectionHeading } from "./Section";
import { Reveal, Stagger, StaggerItem } from "./Reveal";
import { GOOGLE, GOOGLE_REVIEWS } from "../data/site";

const GoogleG = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Stars = ({ n = 5, className = "h-4 w-4" }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${className} ${i < n ? "fill-dash-gold text-dash-gold" : "text-dash-line"}`} />
    ))}
  </div>
);

const initials = (name) => name.trim().split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const AVATAR_COLORS = ["bg-dash-teal", "bg-dash-indigo", "bg-dash-ink", "bg-dash-gold", "bg-dash-navy"];

export const Reviews = () => {
  const hasReviews = GOOGLE_REVIEWS.length > 0;
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading center overline="Patient Reviews" title="What Our Patients Say" subtitle="Feedback from patients on our verified Google Business Profile." />

        {/* rating summary */}
        <Reveal delay={0.05}>
          <div className="mx-auto mt-12 flex max-w-md flex-col items-center gap-3 rounded-[2rem] border border-dash-line bg-dash-bg p-8 text-center shadow-[0_10px_40px_rgb(10,37,64,0.05)]">
            <div className="flex items-center gap-2.5">
              <GoogleG className="h-7 w-7" />
              <span className="font-accent text-lg font-semibold text-dash-ink">Google Reviews</span>
            </div>
            <div className="font-display text-6xl font-bold text-dash-ink leading-none">{GOOGLE.rating.toFixed(1)}</div>
            <Stars n={Math.round(GOOGLE.rating)} className="h-6 w-6" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-dash-teal/10 px-3 py-1 text-xs font-semibold text-dash-teal">
              <ShieldCheck className="h-3.5 w-3.5" /> Verified Google Business Profile
            </span>
          </div>
        </Reveal>

        {hasReviews ? (
          <Stagger className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {GOOGLE_REVIEWS.map((r, i) => (
              <StaggerItem key={i}>
                <div data-testid={`review-card-${i}`} className="group flex h-full flex-col rounded-3xl border border-dash-line bg-dash-bg p-7 transition-all duration-400 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_rgb(10,37,64,0.10)]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-full text-white font-semibold ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}>{initials(r.name)}</span>
                      <div>
                        <div className="font-semibold text-dash-ink leading-tight">{r.name}</div>
                        <Stars n={r.stars} />
                      </div>
                    </div>
                    <GoogleG className="h-5 w-5 opacity-80" />
                  </div>
                  <Quote className="mt-5 h-6 w-6 text-dash-teal/30" />
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-dash-muted">{r.text}</p>
                  <div className="mt-5 flex items-center gap-1.5 text-xs font-medium text-dash-teal">
                    <ShieldCheck className="h-3.5 w-3.5" /> Verified Google Review
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        ) : (
          <Reveal delay={0.1}>
            <p className="mx-auto mt-10 max-w-lg text-center text-dash-muted">
              Read our patients' verified reviews directly on our Google Business Profile.
            </p>
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <div className="mt-12 flex justify-center">
            <a
              href={GOOGLE.url}
              target="_blank"
              rel="noreferrer"
              data-testid="view-all-reviews-btn"
              className="group inline-flex items-center gap-2.5 rounded-full border border-dash-line bg-white px-7 py-4 font-semibold text-dash-ink shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-dash-teal"
            >
              <GoogleG className="h-5 w-5" />
              View All Reviews on Google
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
